import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { Box, useTheme } from '@mui/material';

/**
 * Sparkline — micro-gráfica de tendencia para los tiles de estadística.
 *
 * SVG a mano en vez de una librería de charts: es una polilínea sin ejes, escalas ni tooltip,
 * y el repo ya resuelve así sus gráficos pequeños (ver PlayMetricsDonut).
 *
 * Es decorativa: el dato exacto siempre está en el valor del tile, así que se oculta a
 * lectores de pantalla en lugar de duplicar la cifra.
 */
const VIEW_W = 100;
const PAD_Y = 3;

function Sparkline({ data = [], color = undefined, height = 32, showLastPoint = true }) {
  const theme = useTheme();
  const gradientId = useId();
  const stroke = color || theme.palette.gold.main;

  const points = (data || []).map((n) => Number(n) || 0);
  if (points.length < 2) return null;

  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const stepX = VIEW_W / (points.length - 1);
  const usable = height - PAD_Y * 2;

  const coords = points.map((n, i) => [
    i * stepX,
    // Serie plana (max === min): línea a media altura en vez de dividir por cero.
    max === min ? height / 2 : PAD_Y + (1 - (n - min) / span) * usable
  ]);

  const line = coords.map(([x, y]) => `${x},${y}`).join(' ');
  const area = `${line} ${VIEW_W},${height} 0,${height}`;
  const lastY = coords[coords.length - 1][1];

  return (
    <Box aria-hidden sx={{ position: 'relative', width: '100%', height }}>
      <Box
        component="svg"
        viewBox={`0 0 ${VIEW_W} ${height}`}
        // El eje X se estira al ancho del tile; el trazo se mantiene fino con non-scaling-stroke.
        preserveAspectRatio="none"
        sx={{ width: '100%', height, display: 'block' }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity={0.28} />
            <stop offset="100%" stopColor={stroke} stopOpacity={0} />
          </linearGradient>
        </defs>

        <polygon points={area} fill={`url(#${gradientId})`} />
        <polyline
          points={line}
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </Box>

      {/* El punto final va en CSS y no en el SVG: preserveAspectRatio="none" deforma el eje X y
          un <circle> saldría como elipse. */}
      {showLastPoint && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: `${(lastY / height) * 100}%`,
            width: 5,
            height: 5,
            borderRadius: '50%',
            backgroundColor: stroke,
            transform: 'translate(50%, -50%)'
          }}
        />
      )}
    </Box>
  );
}

Sparkline.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number),
  color: PropTypes.string,
  height: PropTypes.number,
  showLastPoint: PropTypes.bool
};

export default Sparkline;
